/**
 * UranIA PA — Zod schemas onboarding PA (US-015 AC#2).
 *
 * Allineati 1:1 con `services/api-gateway/openapi.yaml` v0.4.0 (US-013):
 *   - `OnboardingPaStep1Anagrafica`
 *   - `OnboardingPaStep2Po`
 *   - `OnboardingPaStep3Fondo`
 *   - `OnboardingPaStep4Ruolo`
 *   - `OnboardingPaStep5Consent`
 *
 * Le regole di forma (lunghezze, pattern, enum) sono replicate qui per
 * dare feedback inline al Funzionario AdG senza dover battere il server.
 * Quando il backend ritorna 422 (es. violazione di business rule non
 * esprimibile in JSON Schema), il messaggio viene mostrato come errore
 * di submit (vedi `OnboardingPaPage` -> `submitError`).
 *
 * Decisione: schemas separati per step (non un grande discriminated
 * union) perche' ogni step renderizza un form indipendente e
 * `useForm({resolver: zodResolver(schema)})` non gestisce bene gli union
 * quando il discriminator e' lo step number passato fuori dal payload.
 */

import { z } from 'zod';

// AC OAS: `^[A-Z0-9]{16}$` — codice fiscale italiano (16 char, maiuscolo).
const codiceFiscalePattern = /^[A-Z0-9]{16}$/u;

export const onboardingPaStep1Schema = z.object({
  nome: z.string().min(1).max(120),
  cognome: z.string().min(1).max(120),
  codice_fiscale: z
    .string()
    .min(16)
    .max(16)
    .regex(codiceFiscalePattern, { message: 'codice_fiscale_invalid' }),
  telefono: z.union([z.string().min(3).max(32), z.literal('')]).optional(),
});
export type OnboardingPaStep1Body = z.infer<typeof onboardingPaStep1Schema>;

export const onboardingPaStep2Schema = z.object({
  po_riferimento: z.string().min(1).max(120),
});
export type OnboardingPaStep2Body = z.infer<typeof onboardingPaStep2Schema>;

export const FONDO_PRINCIPALE_VALUES = ['FESR', 'FSE+'] as const;
export type FondoPrincipale = (typeof FONDO_PRINCIPALE_VALUES)[number];

export const onboardingPaStep3Schema = z.object({
  fondo_principale: z.enum(FONDO_PRINCIPALE_VALUES),
});
export type OnboardingPaStep3Body = z.infer<typeof onboardingPaStep3Schema>;

export const onboardingPaStep4Schema = z.object({
  ruolo_adg: z.string().min(1).max(120),
  esperienza_anni: z.coerce.number().int().min(0).max(80),
});
export type OnboardingPaStep4Body = z.infer<typeof onboardingPaStep4Schema>;

/**
 * Step 5 — consent privacy + AI Act art. 50.
 *
 * Vincolo applicativo (AC US-015 #4 letterale): "Consent checkbox required
 * on step 5 to complete". Replicato letteralmente nella validation: i due
 * boolean DEVONO essere `true` — altrimenti `consent_required_true`.
 */
export const onboardingPaStep5Schema = z.object({
  consent_privacy: z.literal(true, { error: 'consent_required_true' }),
  consent_ai_act: z.literal(true, { error: 'consent_required_true' }),
});
export type OnboardingPaStep5Body = z.infer<typeof onboardingPaStep5Schema>;

export interface OnboardingPaSessionDto {
  readonly session_id: string;
  readonly tenant_id: string;
  readonly user_id: string;
  readonly profile_type: 'pa';
  readonly current_step: number;
  readonly total_steps: 5;
  readonly completed_at?: string | null;
}

export interface OnboardingPaStepResponseDto extends OnboardingPaSessionDto {
  readonly outbox_event_id?: string | null;
  readonly audit_event_id?: string | null;
}

export type OnboardingPaStepNumber = 1 | 2 | 3 | 4 | 5;
export const ONBOARDING_PA_TOTAL_STEPS = 5 as const;
